import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, Button } from '../../styles/GlobalStyles';
import { clubService } from '../../services/clubService';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ClubSubscription, SubscriptionStatus } from '../../types';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  flex-wrap: wrap;
  gap: var(--spacing-md);
`;

const SubscriptionIndicator = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--background-secondary, #f5f5f5);
  border-radius: var(--border-radius);
`;

const TeamsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const TeamCard = styled(Card)`
  padding: var(--spacing-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TeamInfo = styled.div`
  flex: 1;
  min-width: 200px;
`;

const TeamName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
`;

const TeamDetails = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  display: flex;
  gap: var(--spacing-md);
  flex-wrap: wrap;
`;

const TeamActions = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const EmptyState = styled(Card)`
  padding: var(--spacing-xl);
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-sm);
`;

const EmptyStateText = styled.p`
  color: var(--text-secondary);
  margin-bottom: var(--spacing-lg);
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: var(--spacing-md);
`;

const ModalContent = styled(Card)`
  max-width: 500px;
  width: 100%;
  padding: var(--spacing-xl);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-secondary, #f5f5f5);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--border-radius);
  font-size: 1rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  margin-top: var(--spacing-md);
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-md);
  background-color: var(--error-bg, #fee);
  color: var(--error-color, #c00);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  padding: var(--spacing-md);
  background-color: var(--success-bg, #efe);
  color: var(--success-color, #060);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
  font-size: 0.875rem;
`;

const ConfirmDialog = styled(ModalContent)`
  max-width: 400px;
`;

const ConfirmText = styled.p`
  color: var(--text-primary);
  margin-bottom: var(--spacing-lg);
  line-height: 1.5;
`;

interface Team {
  id: string;
  name: string;
  ageGroup: string;
  season: string;
  coachId?: string;
  coachName?: string;
}

export const AdminTeamsPage: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [subscription, setSubscription] = useState<ClubSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    ageGroup: '',
    season: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    if (!user?.profile?.clubId) {
      setError('No club associated with your account');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [teamsData, subscriptionData] = await Promise.all([
        clubService.getClubTeams(user.profile.clubId),
        clubService.getSubscription(user.profile.clubId),
      ]);

      setTeams(teamsData);
      setSubscription(subscriptionData);
    } catch (err: any) {
      console.error('Failed to fetch teams data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load teams data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile?.clubId]);

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.profile?.clubId) {
      setError('No club associated with your account');
      return;
    }

    if (!formData.name.trim() || !formData.ageGroup.trim() || !formData.season.trim()) {
      setError('All fields are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await clubService.createClubTeam(user.profile.clubId, formData);
      
      setSuccess('Team created successfully');
      setIsCreateModalOpen(false);
      setFormData({ name: '', ageGroup: '', season: '' });
      
      // Refresh teams list
      await fetchData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to create team:', err);
      setError(err.response?.data?.error?.message || 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!user?.profile?.clubId || !teamToDelete) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await clubService.deleteClubTeam(user.profile.clubId, teamToDelete.id);
      
      setSuccess('Team removed successfully');
      setIsDeleteModalOpen(false);
      setTeamToDelete(null);
      
      // Refresh teams list
      await fetchData();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete team:', err);
      setError(err.response?.data?.error?.message || 'Failed to remove team');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (team: Team) => {
    setTeamToDelete(team);
    setIsDeleteModalOpen(true);
  };

  const canCreateTeam = (): boolean => {
    if (!subscription || subscription.status !== SubscriptionStatus.ACTIVE) {
      return false;
    }
    return teams.length < subscription.teamLimit;
  };

  const getSubscriptionIndicatorText = (): string => {
    if (!subscription || subscription.status !== SubscriptionStatus.ACTIVE) {
      return 'No active subscription';
    }
    return `${teams.length} of ${subscription.teamLimit} teams used`;
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Teams">
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  const hasActiveSubscription = subscription && subscription.status === SubscriptionStatus.ACTIVE;

  return (
    <AdminLayout pageTitle="Teams">
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <PageHeader>
        <SubscriptionIndicator>{getSubscriptionIndicatorText()}</SubscriptionIndicator>
        {hasActiveSubscription && (
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!canCreateTeam()}
            title={!canCreateTeam() ? 'Subscription limit reached' : 'Create a new team'}
          >
            Create Team
          </Button>
        )}
      </PageHeader>

      {teams.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>No Teams Yet</EmptyStateTitle>
          <EmptyStateText>
            {hasActiveSubscription
              ? 'Create your first team to get started.'
              : 'Teams will appear here once you have an active subscription.'}
          </EmptyStateText>
        </EmptyState>
      ) : (
        <TeamsList>
          {teams.map((team) => (
            <TeamCard key={team.id}>
              <TeamInfo>
                <TeamName>{team.name}</TeamName>
                <TeamDetails>
                  <span>Age Group: {team.ageGroup}</span>
                  <span>Season: {team.season}</span>
                  {team.coachName && <span>Coach: {team.coachName}</span>}
                </TeamDetails>
              </TeamInfo>
              <TeamActions>
                <Button
                  $variant="secondary"
                  onClick={() => {/* TODO: Implement assign coach */}}
                >
                  {team.coachId ? 'Reassign Coach' : 'Assign Coach'}
                </Button>
                <Button
                  $variant="danger"
                  onClick={() => openDeleteModal(team)}
                >
                  Remove
                </Button>
              </TeamActions>
            </TeamCard>
          ))}
        </TeamsList>
      )}

      {/* Create Team Modal */}
      <Modal isOpen={isCreateModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Create Team</ModalTitle>
            <CloseButton onClick={() => setIsCreateModalOpen(false)}>×</CloseButton>
          </ModalHeader>
          <Form onSubmit={handleCreateTeam}>
            <FormGroup>
              <Label htmlFor="teamName">Team Name *</Label>
              <Input
                id="teamName"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Under 12s"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="ageGroup">Age Group *</Label>
              <Input
                id="ageGroup"
                type="text"
                value={formData.ageGroup}
                onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value })}
                placeholder="e.g., U12"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="season">Season *</Label>
              <Input
                id="season"
                type="text"
                value={formData.season}
                onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                placeholder="e.g., 2024/2025"
                required
              />
            </FormGroup>
            <ButtonGroup>
              <Button
                type="button"
                $variant="secondary"
                onClick={() => setIsCreateModalOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Team'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen}>
        <ConfirmDialog>
          <ModalHeader>
            <ModalTitle>Remove Team</ModalTitle>
            <CloseButton onClick={() => setIsDeleteModalOpen(false)}>×</CloseButton>
          </ModalHeader>
          <ConfirmText>
            Are you sure you want to remove "{teamToDelete?.name}"? This action will soft-delete the team
            and it will no longer appear in your teams list.
          </ConfirmText>
          <ButtonGroup>
            <Button
              $variant="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              $variant="danger"
              onClick={handleDeleteTeam}
              disabled={submitting}
            >
              {submitting ? 'Removing...' : 'Remove Team'}
            </Button>
          </ButtonGroup>
        </ConfirmDialog>
      </Modal>
    </AdminLayout>
  );
};
